// What a run compared, in words, and the noise margin to judge it by.

import { formatDate } from 'app/components/ops-datasets/mockData'
import { analyzeModel } from './analysis'
import { DEFAULT_MARGIN } from './stats'
import { PIPELINE_STAGES, type PipelineStage, type PromptSet, type Run } from './types'

const STAGE_NAME: Record<PipelineStage, string> = {
  overview: `Overview`,
  people: `People`,
  appointment: `Appointment`,
  outcome: `Outcome`,
  validation: `Validation`,
}

/** What the labels need from a run, so a run still in progress can be named too. */
type RunSides = Pick<Run, 'candidate' | 'baseline' | 'stages'>

/** Stages whose prompt version differs between two 5-stage sides. A side that runs the
 *  single prompt has no stage prompts, so a run with one has no changed stages. */
export function changedStages(run: RunSides): PipelineStage[] {
  if (run.candidate.single !== undefined || run.baseline.single !== undefined) return []
  return PIPELINE_STAGES.filter((stage) => run.candidate[stage] !== run.baseline[stage])
}

/** Same prompts on both sides: an A/A run that measures run-to-run noise. */
export function isNoiseCheck(run: RunSides): boolean {
  return run.candidate.single === run.baseline.single && changedStages(run).length === 0
}

/** A side's pipeline in words: `Single prompt v1` or `5 stages`. */
const pipelineName = (set: PromptSet) => (set.single === undefined ? `5 stages` : `Single prompt v${set.single}`)

export type RunLabels = {
  /** e.g. "People v18 vs v17", "People v17 vs itself" (a noise check). */
  title: string
  candidate: string
  baseline: string
}

export function runLabels(run: RunSides): RunLabels {
  const { candidate: c, baseline: b } = run
  if (c.single !== undefined || b.single !== undefined) {
    if (c.single === b.single) {
      return { title: `Single prompt v${c.single} vs itself`, candidate: `Second run`, baseline: `First run` }
    }
    const both = c.single !== undefined && b.single !== undefined
    return {
      title: both ? `Single prompt v${c.single} vs v${b.single}` : `${pipelineName(c)} vs ${pipelineName(b)}`,
      candidate: both ? `v${c.single}` : pipelineName(c),
      baseline: both ? `v${b.single}` : pipelineName(b),
    }
  }
  const changed = changedStages(run)
  if (changed.length === 0) {
    // Both sides ran the same prompts, so name the sides by run, not by version.
    const sides = { candidate: `Second run`, baseline: `First run` }
    if (run.stages.length > 1) return { title: `5 stages vs itself`, ...sides }
    const stage = run.stages[0] ?? `people`
    return { title: `${STAGE_NAME[stage]} v${run.candidate[stage]} vs itself`, ...sides }
  }
  if (changed.length === 1) {
    const [stage] = changed
    const candidate = `v${run.candidate[stage]}`
    const baseline = `v${run.baseline[stage]}`
    return { title: `${STAGE_NAME[stage]} ${candidate} vs ${baseline}`, candidate, baseline }
  }
  return {
    title: changed.map((stage) => `${STAGE_NAME[stage]} v${run.candidate[stage]}`).join(`, `) + ` vs baseline`,
    candidate: `The candidate`,
    baseline: `the baseline`,
  }
}

export type NoiseMargin = {
  margin: number
  source: string | null
  /** Calls whose answer changed between the noise check's two identical runs. */
  unstable: Set<string>
}

/**
 * The no-meaningful-difference margin for a run: half the 95% range of the most recent
 * earlier noise check (same dataset, model and execution mode), never under the 2-point
 * default. Identical prompts moving that much means smaller differences are noise.
 */
export function noiseMarginFor(run: Run, model: string, runs: Run[]): NoiseMargin {
  const check = runs
    .filter(
      (other) =>
        other.id !== run.id &&
        isNoiseCheck(other) &&
        other.datasetId === run.datasetId &&
        other.execution === run.execution &&
        other.startedAt <= run.startedAt &&
        other.modelResults.some((result) => result.model === model),
    )
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0]
  if (!check) return { margin: DEFAULT_MARGIN, source: null, unstable: new Set() }

  const result = check.modelResults.find((entry) => entry.model === model)!
  const analysis = analyzeModel(result, check.stages, check.id)
  const unstable = new Set(
    analysis.items.filter((entry) => entry.changes.length > 0).map((entry) => entry.result.itemId),
  )
  const interval = analysis.comparison.interval
  const halfWidth = interval ? (interval.high - interval.low) / 2 : 0
  return halfWidth > DEFAULT_MARGIN
    ? { margin: halfWidth, source: `the noise check on ${formatDate(check.startedAt)}`, unstable }
    : { margin: DEFAULT_MARGIN, source: null, unstable }
}
